/**
 * useEmailCatalogEntityList
 *
 * EMAILS-IMPL-007: shared list+create+update lifecycle for the four
 * EMAILS-IMPL-006 authoring entities (Program/Campaign/Angle/Audience
 * Definition). All four share an identical shape (fetch a list, create one,
 * update one, deterministically keep the visible list in sync afterward)
 * even though their fields/relationships differ -- this hook owns only
 * that shared list/loading/error/mutation bookkeeping; every entity
 * -specific field, validation, and relationship rule stays in its own
 * panel component, mirroring the backend's own "shared mechanics only,
 * explicit per-family business logic" split
 * (email_marketing_catalog_authoring_service.py).
 *
 * Deliberately plain useState/useEffect, matching the existing
 * useEmailCreativeAnalyticsData.js sibling hook's pattern -- no new
 * state-management library.
 *
 * BACKEND COMPATIBILITY NOTE (EMAILS-IMPL-007 characterization): `list()`
 * calls the pre-existing EMAIL-CATALOG-007A read endpoints (GET
 * /email-marketing/programs, etc.), whose response models
 * (ProgramListItem/CampaignListItem/AngleListItem/
 * AudienceDefinitionListItem, email_marketing_catalog_api.py) carry only
 * {id, name, status[, program_id/angle_id for Campaign]} -- no `version`,
 * no `created_at`/`updated_at`. Those richer fields exist only on
 * EMAILS-IMPL-006's own create/update RESPONSE models. A naive
 * "refetch the list after every mutation" strategy would therefore show
 * every row's Version/Created column as blank forever, including the one
 * the operator JUST created or edited. `createItem`/`updateItem` below
 * work around this by upserting the full response object returned from
 * the mutation directly into local state (keyed by `getId`) instead of
 * relying on a subsequent list refetch to surface it -- a frontend-only
 * fix requiring no backend change. Rows the operator has not yet touched
 * this session still only carry the minimal list shape; panels render a
 * "—" placeholder for their missing version/timestamp fields rather than
 * blank. Extending the list endpoints' response shape to include
 * version/timestamps is recorded as an open recommendation for a future
 * checkpoint, not implemented here.
 */
import { useCallback, useEffect, useState } from "react";
import { describeMutationError } from "../lib/emailMarketingApi";

// EMAILS-IMPL-009: extended (not replaced) with an optional `createVersion`
// capability for the 9 governed Asset families -- Program/Campaign/Angle/
// Audience Definition never pass it, so their existing behavior above is
// completely unchanged. Assets also pass `list: async () => []` (no
// `emails`-gated list/read endpoint exists for any Asset family yet -- see
// lib/emailMarketingApi.js's own note above its Asset-authoring section),
// so their visible catalog is session-only: seeded entirely by this
// session's own create/edit/new-Version mutation responses, the same
// upsert-the-response technique already used below for create/update. This
// is a disclosed, real limitation (not a silent shortcut) -- see the
// EMAILS-IMPL-009 completion report's Open Issues.
// BRC0026-PAGINATION-FIX: `listArgs`, `paginated` and `refetchAfterCreate`
// are ADDITIVE and default to the pre-existing behavior, so the eight sibling
// panels (Program/Campaign/Angle/Audience Definition/Assets/Creatives/Sender/
// Footer) -- whose list endpoints are genuinely unbounded and return bare
// arrays -- are completely unaffected. Broadcasts is the only Emails catalog
// list service that is server-bounded, and therefore the only one that needs
// a page envelope.
export function useEmailCatalogEntityList({
  list, create, update, createVersion, getId,
  listArgs = null, refetchAfterCreate = false,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mutating, setMutating] = useState(false);
  const [mutationError, setMutationError] = useState(null);
  // null for a bare-array endpoint -- the absence of a page envelope is
  // reported honestly rather than faked with invented totals.
  const [page, setPage] = useState(null);

  // Serialized so a caller can pass an inline object literal without
  // re-triggering the fetch on every render.
  const listArgsKey = listArgs ? JSON.stringify(listArgs) : null;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const parsed = listArgsKey ? JSON.parse(listArgsKey) : undefined;
      const data = parsed === undefined ? await list() : await list(parsed);
      // Accept BOTH shapes: a bare array (every sibling endpoint) and a
      // {items,total,limit,offset} envelope (Broadcast authoring).
      if (Array.isArray(data)) {
        setItems(data);
        setPage(null);
      } else if (data && Array.isArray(data.items)) {
        setItems(data.items);
        setPage({
          total: data.total ?? data.items.length,
          limit: data.limit ?? data.items.length,
          offset: data.offset ?? 0,
        });
      } else {
        setItems([]);
        setPage(null);
      }
    } catch (err) {
      setError(describeMutationError(err, "Failed to load records.").message);
    } finally {
      setLoading(false);
    }
  }, [list, listArgsKey]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const clearMutationError = useCallback(() => setMutationError(null), []);

  const createItem = useCallback(
    async (payload) => {
      setMutating(true);
      setMutationError(null);
      try {
        const created = await create(payload);
        // BRC0026-PAGINATION-FIX: under a SERVER-ORDERED newest-first page,
        // appending to the end is wrong twice over -- it puts the newest
        // record last, and on any page but the first it injects a row that
        // does not belong to the page being viewed. Callers on a paginated
        // endpoint therefore refetch from the server, which is safe for
        // Broadcasts precisely because its list and create responses are the
        // SAME full `BroadcastResponse` shape -- the reduced-list-shape
        // problem described in this module's BACKEND COMPATIBILITY NOTE
        // above simply does not apply to it. Every other family keeps the
        // original upsert.
        if (refetchAfterCreate) {
          await refresh();
        } else {
          setItems((prev) => [...prev, created]);
        }
        return created;
      } catch (err) {
        setMutationError(describeMutationError(err, "Failed to create the record."));
        throw err;
      } finally {
        setMutating(false);
      }
    },
    [create, refetchAfterCreate, refresh]
  );

  const updateItem = useCallback(
    async (id, payload) => {
      setMutating(true);
      setMutationError(null);
      try {
        const updated = await update(id, payload);
        setItems((prev) => prev.map((item) => (getId(item) === id ? updated : item)));
        return updated;
      } catch (err) {
        setMutationError(describeMutationError(err, "Failed to save changes."));
        throw err;
      } finally {
        setMutating(false);
      }
    },
    [update, getId]
  );

  const createVersionItem = useCallback(
    async (id, payload) => {
      setMutating(true);
      setMutationError(null);
      try {
        const updated = await createVersion(id, payload);
        setItems((prev) => prev.map((item) => (getId(item) === id ? updated : item)));
        return updated;
      } catch (err) {
        setMutationError(describeMutationError(err, "Failed to create the new version."));
        throw err;
      } finally {
        setMutating(false);
      }
    },
    [createVersion, getId]
  );

  return {
    items,
    loading,
    error,
    refresh,
    mutating,
    mutationError,
    clearMutationError,
    createItem,
    updateItem,
    createVersionItem,
    // BRC0026-PAGINATION-FIX: null for bare-array endpoints.
    page,
  };
}

export default useEmailCatalogEntityList;
